import React, { useState, useEffect } from 'react';
import { Trophy, MapPin } from 'lucide-react';
import { useGameStore } from '../../store/gameStore';
import { gameAPI } from '../../services/gameAPI';
import { TurnCounter } from './TurnCounter';

/**
 * GameStatusBar - Top bar displaying read-only game status info
 * Shows: Score, Turn Counter, Current Location
 * Displayed at top of game screen across all breakpoints
 *
 * Self-contained: fetches score from API, reads location from store
 */
export const GameStatusBar = ({
  className = '',
  isTutorial = false  // Tutorial mode shows static 0 score
}) => {
  const {
    sessionId,
    turnCount,
    currentLocation,
    locations,
    selectedTheme
  } = useGameStore();

  const [score, setScore] = useState(0);

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  // Get current location name
  const getLocationName = () => {
    if (!currentLocation) return 'Unknown';
    const location = (locations || []).find(l => l.id === currentLocation);
    return location ? location.name : currentLocation.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Fetch score when turn count changes (skip in tutorial mode)
  useEffect(() => {
    if (isTutorial || !sessionId) return;

    const fetchScore = async () => {
      try {
        const response = await gameAPI.getScore(sessionId);
        setScore(response.score || 0);
      } catch (err) {
        console.error('Error fetching score:', err);
      }
    };
    fetchScore();
  }, [sessionId, turnCount, isTutorial]);

  const locationName = getLocationName();

  return (
    <div className={`flex items-center justify-center gap-3 md:gap-4 px-3 py-2 bg-black/40 border-b border-white/10 ${className}`}>
      {/* Score */}
      <div
        className="flex items-center gap-1.5 px-2 py-1 bg-black/30 border border-white/10 rounded-lg"
        data-tutorial="score-display"
      >
        <Trophy className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: themeAccent }} />
        <span className="text-xs md:text-sm text-white font-medium">{score} pts</span>
      </div>

      {/* Turn Counter */}
      <TurnCounter className="flex-shrink-0" compact />

      {/* Location */}
      <div
        className="flex items-center gap-1.5 px-2 py-1 bg-black/30 border border-white/10 rounded-lg max-w-[160px] md:max-w-[200px] lg:max-w-[40rem]"
        data-tutorial="location-display"
        title={locationName}
      >
        <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" style={{ color: themeAccent }} />
        <span className="text-xs md:text-sm text-white font-medium truncate">{locationName}</span>
      </div>
    </div>
  );
};

export default GameStatusBar;
